<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import listPlugin from "@fullcalendar/list";

import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

import { categories } from "./data-calendar";

import { getBookings } from "../../../api/services/booking.service";
import { getBookingAsEvents } from "./booking-controller";
import EditBookingModel from "./edit-booking";
import CreateBookingModel from "./create-booking";
import EditServiceBookingModel from "./edit-service-booking";
import EditLongBookingModel from "./edit-long-booking";
import ExpiredBookingModal from "./view-expired-booking.vue";
import ExpiredLongBookingModal from "./view-expired-long-booking.vue";
import ExpiredServiceBookingModal from "./view-expired-service-booking.vue";

/**
 * Manage Bookings Page
 */
export default {
	page: {
		title: "Buchungen verwalten",
		meta: [
			{
				name: "description",
				content: appConfig.description,
			},
		],
	},
	components: {
		FullCalendar,
		Layout,
		PageHeader,
		"edit-booking-model": EditBookingModel,
		"create-booking-model": CreateBookingModel,
		"edit-service-booking-model": EditServiceBookingModel,
		"edit-long-booking-model": EditLongBookingModel,
		"view-expired-booking-modal": ExpiredBookingModal,
		"view-expired-long-booking-modal": ExpiredLongBookingModal,
		"view-expired-service-booking-modal": ExpiredServiceBookingModal,
	},
	data() {
		return {
			title: this.$i18n.t("booking.header.title"),
			items: [
				{
					text: this.$i18n.t("booking.header.item"),
					active: true,
				},
			],
			timeZone: "local",
			showEditBookingModel: false,
			showExpiredBookingModal: false,
			showExpiredLongBookingModal: false,
			showExpiredServiceBookingModal: false,
			showEditServiceBookingModel: false,
			showEditLongBookingModel: false,
			showCreateBookingModel: false,
			bookingToEdit: null,
			calendarEvents: null,
			calendarOptions: {
				headerToolbar: {
					left: "prev,next today",
					center: "title",
					right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
				},
				plugins: [
					dayGridPlugin,
					timeGridPlugin,
					interactionPlugin,
					bootstrapPlugin,
					listPlugin,
				],
				locale: "de-at",
				initialView: "dayGridMonth",
				themeSystem: "bootstrap",
				events: null,
				editable: false,
				droppable: true,
				eventResizableFromStart: true,
				dateClick: this.createEvent,
				eventClick: this.editEvent,
				eventsSet: this.handleEvents,
				weekends: true,
				selectable: true,
				selectMirror: true,
				dayMaxEvents: false,
				newEventData: {},
			},
			currentEvents: [],
			categories: categories,
		};
	},
	async mounted() {
		await this.getAllBookings();
	},
	methods: {
		async getAllBookings() {
			let getAllBookingsFrom = await getBookings();
			let getAllEvents = await getBookingAsEvents(getAllBookingsFrom);
			// make tect to string
			// for (let i = 0; i < getAllEvents.length; i++) {
			//   getAllEvents[i].start = getAllEvents[i].start.toString().slice(0, 19);
			//   getAllEvents[i].end = getAllEvents[i].end.toString().slice(0, 19);
			// }
			this.calendarEvents = getAllEvents;
			this.calendarOptions.events = getAllEvents;
		},
		realodPage() {
			window.location.reload();
		},
		editEvent(info) {
			this.bookingToEdit = info.event.extendedProps;
			if (this.bookingToEdit.bookingPassed === true) {
				if (this.bookingToEdit.type === "OUT_OF_SERVICE") {
					this.showExpiredServiceBookingModal = true;
				} else if (this.bookingToEdit.type === "GENERAL") {
					this.showExpiredBookingModal = true;
				} else {
					this.showExpiredLongBookingModal = true;
				}
			} else if (this.bookingToEdit.bookingPassed === false) {
				if (this.bookingToEdit.type === "OUT_OF_SERVICE") {
					this.showEditServiceBookingModel = true;
				} else if (this.bookingToEdit.type === "GENERAL") {
					this.showEditBookingModel = true;
				} else {
					this.showEditLongBookingModel = true;
				}
			}
		},
		createEvent(info) {
			this.newEventData = info;
			this.showCreateBookingModel = true;
		},
	},
};
</script>

<template>
	<Layout>
		<PageHeader :title="title" :items="items" />
		<div class="row justify-content-center">
			<div v-if="this.calendarEvents" class="col-12">
				<div class="card">
					<div class="card-body">
						<div class="app-calendar">
							<FullCalendar
								ref="fullCalendar"
								:options="calendarOptions"
								:timeZone="timeZone"
							></FullCalendar>
						</div>
					</div>
				</div>
			</div>
			<div v-else>
				<div class="col-12 text-center p-5">
					<b-spinner
						style="width: 5rem; height: 5rem"
						class="m-2"
						variant="mid"
						role="status"
					></b-spinner>
				</div>
			</div>
		</div>

		<!-- Edit Booking Model -->
		<edit-booking-model
			v-if="showEditBookingModel === true"
			:visible="showEditBookingModel"
			@close="
				showEditBookingModel = false;
				getAllBookings();
			"
			@reload="realodPage()"
			:booking="bookingToEdit"
		/>

		<!-- Edit Service Booking Model -->
		<edit-service-booking-model
			v-if="showEditServiceBookingModel === true"
			:visible="showEditServiceBookingModel"
			@close="showEditServiceBookingModel = false"
			@reload="realodPage()"
			:booking="bookingToEdit"
		/>

		<!-- Edit Long Booking Model -->
		<edit-long-booking-model
			v-if="showEditLongBookingModel === true"
			:visible="showEditLongBookingModel"
			@close="showEditLongBookingModel = false"
			@reload="realodPage()"
			:booking="bookingToEdit"
		/>

		<!--Create Booking Model -->
		<create-booking-model
			v-if="showCreateBookingModel === true"
			:visible="showCreateBookingModel"
			@close="
				showCreateBookingModel = false;
				getAllBookings();
			"
			@reload="realodPage()"
			:newBooking="newEventData"
		/>

		<!-- Expire  Booking Model -->
		<view-expired-booking-modal
			v-if="showExpiredBookingModal === true"
			:visible="showExpiredBookingModal"
			@close="
				showExpiredBookingModal = false;
				getAllBookings();
			"
			@reload="realodPage()"
			:booking="bookingToEdit"
		/>

		<!-- Expire Long Booking Model -->
		<view-expired-long-booking-modal
			v-if="showExpiredLongBookingModal === true"
			:visible="showExpiredLongBookingModal"
			@close="
				showExpiredLongBookingModal = false;
				getAllBookings();
			"
			@reload="realodPage()"
			:booking="bookingToEdit"
		/>

		<!-- Expire Service Booking Model -->
		<view-expired-service-booking-modal
			v-if="showExpiredServiceBookingModal === true"
			:visible="showExpiredServiceBookingModal"
			@close="
				showExpiredServiceBookingModal = false;
				getAllBookings();
			"
			@reload="realodPage()"
			:booking="bookingToEdit"
		/>
	</Layout>
</template>
